import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { bool, func, string } from 'prop-types';
import React, { memo, useCallback, useState } from 'react';
import { I18n } from 'utils/i18n';

const ROLES = ['admin', 'member', 'guest'];

export const membershipRoleLabel = ({ role, isVisible }) => {
  const roleLabel = role ? I18n.t(`Roles.${role}`) : '';
  if (!isVisible) return roleLabel;
  return `${roleLabel} · ${I18n.t('TeamDrawer.EmployeesTable.Visible')}`;
};

const TeamMembershipRoleSelect = ({
  role = 'member',
  isVisible = false,
  onChange,
  className = null,
  visibilityDescriptionKey = 'TeamDrawer.EmployeesTable.VisibleToGuestsDescription',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [localRole, setLocalRole] = useState(role || 'member');
  const [localIsVisible, setLocalIsVisible] = useState(!!isVisible);

  const handleOpen = useCallback(
    event => {
      event.stopPropagation();
      setLocalRole(role || 'member');
      setLocalIsVisible(!!isVisible);
      setAnchorEl(event.currentTarget);
    },
    [role, isVisible],
  );

  const handleClose = useCallback(event => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  }, []);

  const handleApply = useCallback(
    event => {
      event.stopPropagation();
      onChange({ role: localRole, isVisible: localIsVisible });
      setAnchorEl(null);
    },
    [localIsVisible, localRole, onChange],
  );

  return (
    <>
      <Select
        className={className}
        sx={className ? undefined : { width: 150 }}
        value=""
        open={false}
        onOpen={handleOpen}
        onClick={event => event.stopPropagation()}
        displayEmpty
        renderValue={() => membershipRoleLabel({ role, isVisible })}
        MenuProps={{ onClick: event => event.stopPropagation() }}
      >
        <MenuItem value="" />
      </Select>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={event => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
            {I18n.t('TeamDrawer.EmployeesTable.BaseRole')}
          </Typography>
          <RadioGroup value={localRole} onChange={event => setLocalRole(event.target.value)}>
            {ROLES.map(nextRole => (
              <FormControlLabel
                key={nextRole}
                value={nextRole}
                control={<Radio size="small" color="primary" />}
                label={I18n.t(`Roles.${nextRole}`)}
              />
            ))}
          </RadioGroup>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
            {I18n.t('TeamDrawer.EmployeesTable.Visibility')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={localIsVisible}
                  onChange={event => setLocalIsVisible(event.target.checked)}
                />
              }
              label={I18n.t('TeamDrawer.EmployeesTable.VisibleToGuests')}
            />
            <Tooltip title={I18n.t(visibilityDescriptionKey)} placement="top">
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mt: 2,
            }}
          >
            <Button variant="contained" size="small" color="primary" onClick={handleApply}>
              {I18n.t('TeamDrawer.EmployeesTable.ApplyChanges')}
            </Button>
            <Button variant="outlined" size="small" onClick={handleClose}>
              {I18n.t('TeamDrawer.EmployeesTable.Cancel')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

TeamMembershipRoleSelect.propTypes = {
  role: string,
  isVisible: bool,
  onChange: func.isRequired,
  className: string,
  visibilityDescriptionKey: string,
};

export default memo(TeamMembershipRoleSelect);
